<template>
  <div>
    <!-- Step: Choose method -->
    <template v-if="step === 'method'">
      <p class="text-gray-400 mb-3 text-xs tracking-wide uppercase">
        Kies een betaalmethode
      </p>
      <div class="flex flex-col gap-2.5">
        <!-- iDeal -->
        <button
          class="flex items-center gap-4 p-4 rounded-2xl border-2 bg-white transition-all text-left"
          :class="
            selectedMethod === 'ideal'
              ? 'border-green-600 shadow-sm'
              : 'border-gray-100 hover:border-gray-200'
          "
          @click="$emit('select-method', 'ideal')"
        >
          <div
            class="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center"
          >
            <span class="text-white text-xs font-bold">iDEAL</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-gray-800 text-sm">iDeal</span>
            </div>
            <span class="text-gray-400 text-xs">Direct via je eigen bank</span>
          </div>
          <div class="flex-shrink-0">
            <div
              v-if="selectedMethod === 'ideal'"
              class="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center"
            >
              <svg
                class="w-3.5 h-3.5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <svg
              v-else
              class="w-5 h-5 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </button>

        <!-- PayPal -->
        <button
          class="flex items-center gap-4 p-4 rounded-2xl border-2 bg-white transition-all text-left"
          :class="
            selectedMethod === 'paypal'
              ? 'border-green-600 shadow-sm'
              : 'border-gray-100 hover:border-gray-200'
          "
          @click="$emit('select-method', 'paypal')"
        >
          <div
            class="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-900 flex items-center justify-center"
          >
            <span class="text-white text-xs font-bold">PP</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-gray-800 text-sm">PayPal</span>
              <span
                v-if="paypalFee > 0"
                class="text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full text-xs"
              >
                +{{ $n(paypalFee, 'currency') }}
              </span>
            </div>
            <span class="text-gray-400 text-xs">
              Betaal met je PayPal account
            </span>
          </div>
          <div class="flex-shrink-0">
            <div
              v-if="selectedMethod === 'paypal'"
              class="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center"
            >
              <svg
                class="w-3.5 h-3.5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <svg
              v-else
              class="w-5 h-5 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </button>
      </div>
    </template>

    <!-- Step: Choose bank (iDeal) -->
    <template v-if="step === 'bank'">
      <div class="flex items-center gap-2 mb-3">
        <p class="text-gray-400 text-xs tracking-wide uppercase">
          Kies je bank
        </p>
      </div>

      <!-- Back to methods pill -->
      <button
        class="mb-3 flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-white border border-gray-100 hover:bg-gray-50 transition-colors w-full"
        @click="$emit('change-method')"
      >
        <svg
          class="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        <span class="text-gray-500 flex-1 text-center text-sm">
          Kies andere betaalmethode
        </span>
      </button>

      <div class="flex flex-col gap-2">
        <button
          v-for="bank in banks"
          :key="bank.key"
          class="flex items-center gap-3.5 p-3.5 rounded-2xl border-2 bg-white transition-all text-left"
          :class="
            selectedBank === bank.key
              ? 'border-green-600 shadow-sm'
              : 'border-gray-100 hover:border-gray-200'
          "
          @click="$emit('select-bank', bank.key)"
        >
          <!-- Bank logo -->
          <div
            class="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden"
          >
            <img
              v-if="bank.image"
              :src="bank.image"
              :alt="bank.value"
              class="w-7 h-7 object-contain"
            />
            <span v-else class="text-gray-500 text-xs font-bold">
              {{ bank.value.slice(0, 2).toUpperCase() }}
            </span>
          </div>

          <!-- Name -->
          <span class="flex-1 text-gray-700 text-sm">
            {{ bank.value }}
          </span>

          <!-- Check -->
          <div
            v-if="selectedBank === bank.key"
            class="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0"
          >
            <svg
              class="w-3.5 h-3.5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'PaymentOptions',
  props: {
    step: {
      type: String,
      default: 'method'
    },
    selectedMethod: {
      type: String,
      default: null
    },
    selectedBank: {
      type: String,
      default: null
    },
    paypalFee: {
      type: [String, Number],
      default: 0
    },
    banks: {
      type: Array,
      default: () => []
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
</style>
